import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { TimeRequestKind } from "../data";
import { useTimeRequestMutations } from "../useTimeRequestApi";

type IFormInputs = {
	action: "APPROVE" | "DECLINE" | "";
	comment?: string;
};

type TimeRequestResponseModalProps = {
	kind: TimeRequestKind;
	request: any;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onResponded: () => void;
};

const TimeRequestResponseModal = ({
	kind,
	request,
	open,
	setOpen,
	onResponded,
}: TimeRequestResponseModalProps) => {
	const [saving, setSaving] = useState(false);
	const { respond } = useTimeRequestMutations(kind);
	const {
		register,
		watch,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({ defaultValues: { action: "" } });

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setSaving(true);
		respond.mutate(
			{
				id: request.id,
				payload: { action: data.action, comment: data.comment || undefined },
			},
			{
				onSuccess: () => {
					onResponded();
					handleClose();
				},
				onSettled: () => setSaving(false),
			},
		);
	};

	const isLastLevel = (request?.currentLevel ?? 1) >= (request?.approvalsRequired ?? 1);

	return (
		<ModalComponent
			width={600}
			open={open}
			title="Respond to Request"
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="flex flex-col gap-y-6">
					<p className="text-2sm text-gray-600">
						{isLastLevel
							? "This is the final approval; approving adds the time to payroll."
							: `This is approval level ${request?.currentLevel ?? 1} of ${request?.approvalsRequired}.`}
					</p>
					<div className="w-full">
						<Label title="Action" for="action" />
						<select
							id="action"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.action
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("action", { required: "Select an action" })}
						>
							<option value="">Select Action</option>
							<option value="APPROVE">Approve</option>
							<option value="DECLINE">Decline</option>
						</select>
						{errors.action && (
							<p className="text-red-500 text-sm mt-1">{errors.action.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Comment" for="comment" optional={watch("action") !== "DECLINE"} />
						<textarea
							id="comment"
							rows={3}
							placeholder="Enter comment"
							className={`w-full bg-secondary-gray rounded-lg px-5 py-4 mt-2 text-sm ${
								errors.comment
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("comment", {
								validate: (value) =>
									watch("action") !== "DECLINE"
									|| !!value?.trim()
									|| "Give a reason for declining",
							})}
						/>
						{errors.comment && (
							<p className="text-red-500 text-sm mt-1">{errors.comment.message}</p>
						)}
					</div>
				</div>
				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={saving ? "Submitting..." : "Submit"}
						className="px-8"
						disabled={saving}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default TimeRequestResponseModal;
